import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

export type JournalHistoryPanel = 'notes' | 'files' | 'markings';

export function JournalHistoryModeTabs({
  idBase,
  value,
  onChange,
}: {
  idBase: string;
  value: JournalHistoryPanel;
  onChange: (value: JournalHistoryPanel) => void;
}) {
  const { t } = useTranslation();
  const tabs: { id: JournalHistoryPanel; label: string }[] = [
    { id: 'notes', label: t('journal.history_tab_notes') },
    { id: 'files', label: t('journal.history_tab_files') },
    { id: 'markings', label: t('journal.history_tab_markings') },
  ];

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key !== 'ArrowRight' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'Home' &&
      event.key !== 'End'
    ) {
      return;
    }
    event.preventDefault();
    const index = Math.max(
      0,
      tabs.findIndex((tab) => tab.id === value),
    );
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else next = tabs.length - 1;
    const target = tabs[next];
    if (!target) return;
    onChange(target.id);
    const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    buttons[next]?.focus();
  };

  return (
    <div
      className="jw-tabs"
      role="tablist"
      aria-label={t('journal.previous_notes')}
      onKeyDown={onKeyDown}
    >
      {tabs.map((tab) => {
        const selected = tab.id === value;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`${idBase}-${tab.id}`}
            aria-controls={`${idBase}-${tab.id}-panel`}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            data-testid={`journal-history-tab-${tab.id}`}
            onClick={() => onChange(tab.id)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
